import { type LinkProps, Link } from "@tanstack/react-router";
import { type Api } from "api-client";
import { useTranslation } from "react-i18next";

import { QuestionnaireSubmissionNavigationButton } from "src/components/QuestionnaireSubmissionNavigationButton";

export interface SubmissionNavigationProps {
  submission: Api.GetSubmission.Output.Self;
  prevLinkProps?: LinkProps;
  nextLinkProps?: LinkProps;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  position?: {
    current: number;
    total: number;
  };
  showAthleteLink?: boolean;
  standalone?: boolean;
}

export function QuestionnaireSubmissionNavigation(
  props: SubmissionNavigationProps,
) {
  const { t } = useTranslation();
  const showAthleteLink = props.showAthleteLink ?? true;
  const respondentName =
    props.submission.profileLead?.name ??
    props.submission.athlete?.user.name ??
    "-";
  const hasPrevious = props.hasPrevious ?? props.submission.navigation.hasPrev;
  const hasNext = props.hasNext ?? props.submission.navigation.hasNext;

  return (
    <div
      className={`flex items-center p-4 border mb-4 gap-4 ${
        props.standalone ? "rounded-md" : "rounded-b-md"
      }`}
    >
      <div className="flex items-center pl-4 gap-4 border rounded-md overflow-hidden">
        {showAthleteLink && props.submission.athlete ? (
          <Link
            to="/athletes/$athleteId/profile"
            params={{ athleteId: props.submission.athlete.id }}
            className="text-sm hover:underline"
          >
            {respondentName}
          </Link>
        ) : (
          <span className="text-sm">{respondentName}</span>
        )}
        {props.position ? (
          <span className="text-sm text-muted-foreground">
            {t("questionnaireSubmissionNavigation.position", {
              current: props.position.current,
              total: props.position.total,
            })}
          </span>
        ) : null}
        <div className="flex rounded-l-sm overflow-hidden">
          <QuestionnaireSubmissionNavigationButton
            label={t("questionnaireSubmissionNavigation.previous")}
            disabled={!hasPrevious}
            linkProps={props.prevLinkProps}
            onClick={props.onPrevious}
            icon="tabler-chevron-left"
          />
          <QuestionnaireSubmissionNavigationButton
            label={t("questionnaireSubmissionNavigation.next")}
            disabled={!hasNext}
            linkProps={props.nextLinkProps}
            onClick={props.onNext}
            icon="tabler-chevron-right"
          />
        </div>
      </div>
      {props.submission.submittedAt ? (
        <div className="text-sm flex text-muted-foreground">
          {t("questionnaireSubmissionNavigation.submitted")}
          {": "}
          {t("dateTime", {
            val: new Date(props.submission.submittedAt),
            formatParams: {
              val: {
                hour: "numeric",
                minute: "numeric",
                year: "numeric",
                month: "long",
                day: "numeric",
              },
            },
          })}
        </div>
      ) : null}
    </div>
  );
}
